/* Customer shop catalog realtime sync.
 *
 * Handler order (tenant-wide before per-kiosk), matching rpapp-kiosk useProducts:
 *   1. product_media_updated — tenant-wide focal + client cache-bust (`v`) + urgent refetch
 *      (IMG-014 metadata-only; no imageUrl in SSE — see patchCatalogProductMedia)
 *   2. product_barcode_updated — tenant-wide refetch
 *   3. catalog_media_settings_updated — invalidate shop-config
 *   4. inventory_updated — per-sales-point filter
 *   5. product_created / product_updated — tenant-wide coalesced refetch
 *   6. product_deleted — optimistic remove + refetch
 *   7. variant_created / variant_updated — tenant-wide coalesced refetch
 *   8. variant_deleted / variant_archived — optimistic variant remove + refetch
 *   9. variant_inventory_updated — per-sales-point filter; optimistic qty/hide + refetch
 *   7. SSE connect/reconnect — coalesced refetch (Contract B / G2):
 *      kiosk `handleSSEConnect` 30s cooldown across reconnect flaps;
 *      G10 storm catch-up via explicit storm-resume bypass (not cooldown zero).
 *
 * SSE fully unavailable: useShopProducts keeps ~30s poll
 * (`PRODUCT_CATALOG_REFETCH_INTERVAL_MS`); no optimistic media cache-bust until poll.
 */

import { startTransition, useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  patchCatalogProductMedia,
  type CatalogProductMediaPatch,
  createCoalescedRefetchScheduler,
  type CoalescedRefetchScheduler,
} from 'pi-kiosk-shared';
import {
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import {
  invalidateShopKioskMetaCache,
} from '../../../shared/api/shopKioskMetaCacheInvalidate.js';
import {
  isKioskUpdated,
  readSalesPointIdFromRealtimeMessage,
} from '../../../shared/api/shopKioskMetaRealtimeEvents.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import type { CatalogProduct } from '../types.js';
import { useShopKioskSSE } from './useShopKioskSSE.js';

/**
 * Match kiosk `handleSSEConnect` — coalesce connect/reconnect catalog refresh.
 * Contract B (G2): cooldown survives ordinary disconnect; only storm latch arm
 * sets an explicit catch-up bypass (see stormCatchUpPendingRef).
 */
export const SSE_PRODUCT_REFRESH_COOLDOWN_MS = 30_000;

interface ProductUpdateMessage {
  type?: string;
  updateType?: string;
  data?: Record<string, unknown>;
}

function parseProductMediaPatch(
  data: Record<string, unknown> | undefined
): CatalogProductMediaPatch | null {
  const productId = data?.productId;
  if (typeof productId !== 'number') {
    return null;
  }

  const variantIdRaw = data?.variantId;
  let variantId: number | null | undefined;
  if (variantIdRaw === null) {
    variantId = null;
  } else if (typeof variantIdRaw === 'number') {
    variantId = variantIdRaw;
  } else {
    variantId = undefined;
  }

  return {
    productId,
    variantId,
    primaryImageId:
      typeof data?.primaryImageId === 'number' || data?.primaryImageId === null
        ? (data.primaryImageId as number | null)
        : undefined,
    catalogSequence:
      typeof data?.catalogSequence === 'number' ? data.catalogSequence : undefined,
    focalPointX:
      typeof data?.focalPointX === 'number' || data?.focalPointX === null
        ? (data.focalPointX as number | null)
        : undefined,
    focalPointY:
      typeof data?.focalPointY === 'number' || data?.focalPointY === null
        ? (data.focalPointY as number | null)
        : undefined,
    cropZoom:
      typeof data?.cropZoom === 'number' || data?.cropZoom === null
        ? (data.cropZoom as number | null)
        : undefined,
    updatedAt: typeof data?.updatedAt === 'string' ? data.updatedAt : undefined,
  };
}

function removeVariantFromCatalog(
  products: CatalogProduct[],
  productId: number,
  variantId: number,
): CatalogProduct[] {
  return products.map((product) => {
    if (product.id !== productId || !product.variants?.length) {
      return product;
    }
    const variants = product.variants.filter((variant) => variant.id !== variantId);
    return {
      ...product,
      variants,
      variantCount: variants.length,
    };
  });
}

function patchVariantInventoryInCatalog(
  products: CatalogProduct[],
  productId: number,
  variantId: number,
  patch: { quantityInStock?: number; active?: boolean },
): CatalogProduct[] {
  return products.map((product) => {
    if (product.id !== productId || !product.variants?.length) {
      return product;
    }
    const variants = product.variants.map((variant) => {
      if (variant.id !== variantId) {
        return variant;
      }
      return {
        ...variant,
        ...(typeof patch.quantityInStock === 'number'
          ? { quantityInStock: patch.quantityInStock }
          : {}),
        ...(typeof patch.active === 'boolean' ? { active: patch.active } : {}),
      };
    });
    return { ...product, variants };
  });
}

export function useShopCatalogRealtimeSync(
  kioskId: number | null,
  cacheKey: QueryKey,
  onBeforeCatalogRefetch?: () => void,
): boolean {
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const kioskIdRef = useRef(kioskId);
  const cacheKeyRef = useRef(cacheKey);
  const tenantCodeRef = useRef(tenantCode);
  const queryClientRef = useRef(queryClient);
  const coalescedRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);
  const onBeforeCatalogRefetchRef = useRef(onBeforeCatalogRefetch);
  const lastSseProductRefreshRef = useRef(0);
  /** G10 — next SSE open bypasses connect-refresh cooldown after storm latch arm. */
  const stormCatchUpPendingRef = useRef(false);

  useEffect(() => {
    kioskIdRef.current = kioskId;
    cacheKeyRef.current = cacheKey;
    tenantCodeRef.current = tenantCode;
    queryClientRef.current = queryClient;
    onBeforeCatalogRefetchRef.current = onBeforeCatalogRefetch;
  }, [kioskId, cacheKey, tenantCode, queryClient, onBeforeCatalogRefetch]);

  useEffect(() => {
    if (coalescedRefetchRef.current === null) {
      coalescedRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => cacheKeyRef.current,
      );
    }
    const coalescedRefetch = coalescedRefetchRef.current;
    return (): void => {
      coalescedRefetch.cancel();
    };
  }, []);

  // G10 / Contract B — arm catch-up when storm latches (incl. already-latched mount).
  // Do not zero lastSseProductRefreshRef on ordinary disconnect (kiosk cooldown parity).
  useEffect(() => {
    if (isHttpStormLatched()) {
      stormCatchUpPendingRef.current = true;
    }
    return subscribeHttpStormLatch(() => {
      if (isHttpStormLatched()) {
        stormCatchUpPendingRef.current = true;
      }
    });
  }, []);

  const scheduleCatalogRefetch = useCallback((): void => {
    onBeforeCatalogRefetchRef.current?.();
    coalescedRefetchRef.current?.schedule();
  }, []);

  const isConnected = useShopKioskSSE(kioskId);

  useEffect(() => {
    if (!isConnected) {
      // Contract B (G2): keep lastSseProductRefreshRef across disconnect so
      // non-storm reconnect flaps honor the 30s cooldown (kiosk handleSSEConnect).
      // Storm catch-up is stormCatchUpPendingRef only — never reset cooldown here.
      return;
    }
    const now = Date.now();
    const stormBypass = stormCatchUpPendingRef.current;
    if (stormBypass) {
      stormCatchUpPendingRef.current = false;
    } else if (now - lastSseProductRefreshRef.current < SSE_PRODUCT_REFRESH_COOLDOWN_MS) {
      return;
    }
    lastSseProductRefreshRef.current = now;
    // onBeforeCatalogRefetch (clearCatalogEtag) runs inside scheduleCatalogRefetch.
    scheduleCatalogRefetch();
  }, [isConnected, scheduleCatalogRefetch]);

  useEffect(() => {
    const handleWebSocketMessage = (event: Event): void => {
      try {
        const customEvent = event as CustomEvent<{ data: string }>;
        const message = JSON.parse(customEvent.detail.data) as ProductUpdateMessage;

        if (isKioskUpdated(message)) {
          const currentKioskId = kioskIdRef.current;
          const eventSalesPointId = readSalesPointIdFromRealtimeMessage(message);
          if (
            currentKioskId !== null &&
            currentKioskId > 0 &&
            (eventSalesPointId === undefined || eventSalesPointId === currentKioskId)
          ) {
            invalidateShopKioskMetaCache(
              queryClientRef.current,
              tenantCodeRef.current,
              currentKioskId,
            );
          }
          return;
        }

        if (message.type !== 'product_update') {
          return;
        }

        const currentKioskId = kioskIdRef.current;
        if (currentKioskId === null || currentKioskId <= 0) {
          return;
        }

        if (message.updateType === 'product_media_updated') {
          const patch = parseProductMediaPatch(message.data);
          if (patch === null) {
            return;
          }

          queryClient.setQueryData<CatalogProduct[]>(
            cacheKeyRef.current,
            (oldData) => {
              if (!oldData || !Array.isArray(oldData)) {
                return oldData ?? [];
              }
              return patchCatalogProductMedia(oldData, patch);
            }
          );
          scheduleCatalogRefetch();
          return;
        }

        if (message.updateType === 'product_barcode_updated') {
          startTransition(() => {
            void queryClient.invalidateQueries({ queryKey: cacheKeyRef.current });
          });
          scheduleCatalogRefetch();
          return;
        }

        if (message.updateType === 'catalog_media_settings_updated') {
          const shopConfigKey = customerQueryKeys.shopConfig(
            tenantCodeRef.current,
            currentKioskId
          );
          startTransition(() => {
            void queryClient.invalidateQueries({ queryKey: shopConfigKey });
            void queryClient.refetchQueries({
              queryKey: shopConfigKey,
              type: 'active',
            });
          });
          return;
        }

        if (message.updateType === 'category_updated') {
          scheduleCatalogRefetch();
          return;
        }

        if (message.updateType === 'inventory_updated') {
          // Match kiosk: backend sends salesPointId; keep legacy kioskId fallback.
          const targetKioskIdNum = Number(
            message.data?.salesPointId ?? message.data?.kioskId
          );
          const currentKioskIdNum = Number(currentKioskId);
          const productId = message.data?.productId;
          const active = message.data?.active;

          if (
            targetKioskIdNum !== currentKioskIdNum ||
            Number.isNaN(targetKioskIdNum) ||
            Number.isNaN(currentKioskIdNum)
          ) {
            return;
          }

          if (active === false && typeof productId === 'number') {
            // Match kiosk: optimistic remove + coalesced refetch (clears ETag via onBefore).
            queryClient.setQueryData<CatalogProduct[]>(
              cacheKeyRef.current,
              (oldData) => {
                if (!oldData || !Array.isArray(oldData)) {
                  return [];
                }
                return oldData.filter((p) => p.id !== productId);
              }
            );
            scheduleCatalogRefetch();
          } else {
            scheduleCatalogRefetch();
          }
          return;
        }

        if (
          message.updateType === 'product_created' ||
          message.updateType === 'product_updated'
        ) {
          scheduleCatalogRefetch();
          return;
        }

        if (message.updateType === 'product_deleted') {
          const deletedProductId = message.data?.productId;
          if (typeof deletedProductId === 'number') {
            queryClient.setQueryData<CatalogProduct[]>(
              cacheKeyRef.current,
              (oldData) => {
                if (!oldData || !Array.isArray(oldData)) {
                  return [];
                }
                return oldData.filter((p) => p.id !== deletedProductId);
              }
            );
          }
          startTransition(() => {
            void queryClient.invalidateQueries({ queryKey: cacheKeyRef.current });
          });
          onBeforeCatalogRefetchRef.current?.();
          return;
        }

        if (
          message.updateType === 'variant_created' ||
          message.updateType === 'variant_updated'
        ) {
          scheduleCatalogRefetch();
          return;
        }

        if (
          message.updateType === 'variant_deleted' ||
          message.updateType === 'variant_archived'
        ) {
          const productId = message.data?.productId;
          const variantId = message.data?.variantId;
          if (typeof productId === 'number' && typeof variantId === 'number') {
            queryClient.setQueryData<CatalogProduct[]>(
              cacheKeyRef.current,
              (oldData) => {
                if (!oldData || !Array.isArray(oldData)) {
                  return oldData ?? [];
                }
                return removeVariantFromCatalog(oldData, productId, variantId);
              }
            );
          }
          scheduleCatalogRefetch();
          return;
        }

        if (message.updateType === 'variant_inventory_updated') {
          const targetKioskIdNum = Number(
            message.data?.salesPointId ?? message.data?.kioskId
          );
          const currentKioskIdNum = Number(currentKioskId);
          const productId = message.data?.productId;
          const variantId = message.data?.variantId;
          const active = message.data?.active;
          const quantityInStock = message.data?.quantityInStock;

          if (
            targetKioskIdNum !== currentKioskIdNum ||
            Number.isNaN(targetKioskIdNum) ||
            Number.isNaN(currentKioskIdNum) ||
            typeof productId !== 'number' ||
            typeof variantId !== 'number'
          ) {
            return;
          }

          if (active === false) {
            queryClient.setQueryData<CatalogProduct[]>(
              cacheKeyRef.current,
              (oldData) => {
                if (!oldData || !Array.isArray(oldData)) {
                  return oldData ?? [];
                }
                return removeVariantFromCatalog(oldData, productId, variantId);
              }
            );
            scheduleCatalogRefetch();
            return;
          }

          if (
            typeof quantityInStock === 'number' ||
            typeof active === 'boolean'
          ) {
            queryClient.setQueryData<CatalogProduct[]>(
              cacheKeyRef.current,
              (oldData) => {
                if (!oldData || !Array.isArray(oldData)) {
                  return oldData ?? [];
                }
                return patchVariantInventoryInCatalog(oldData, productId, variantId, {
                  ...(typeof quantityInStock === 'number'
                    ? { quantityInStock }
                    : {}),
                  ...(typeof active === 'boolean' ? { active } : {}),
                });
              }
            );
          }
          scheduleCatalogRefetch();
          return;
        }

        scheduleCatalogRefetch();
      } catch {
        return;
      }
    };

    const handleForceRefresh = (): void => {
      scheduleCatalogRefetch();
    };

    window.addEventListener('websocket-message', handleWebSocketMessage);
    window.addEventListener('force-refresh', handleForceRefresh);

    return (): void => {
      window.removeEventListener('websocket-message', handleWebSocketMessage);
      window.removeEventListener('force-refresh', handleForceRefresh);
    };
  }, [queryClient, scheduleCatalogRefetch]);

  return isConnected;
}
